import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import AgGrid from '../../shared/agGrid/agGrid.component';

import { userClientListPropTypes } from '../../shared/propTypes/userClients';
import {
  rolesOptionsPropTypes,
  clientRolePropTypes,
  clientExpirationDatePropTypes,
} from '../propTypes';
import getColumnDefs from '../../shared/columnDefinitions/userClients';

const UserClientsGrid = ({
  clients,
  readOnly,
  value,
  checkClient,
  selectRole,
  rolesOptions,
  rolesValues,
  onGridReady,
  loggedInUser,
  expirationDateValues,
  changeExpirationDate,
  isLoading,
  disabled,
}) => {
  const onCheckClient = useCallback((value, checked) => {
    const clientToCheck = clients.find(({ clientId }) => clientId === value);
    checkClient(clientToCheck, checked);
  }, [clients, checkClient]);

  const findRole = useCallback(clientId => {
    const role = rolesValues.find(role => role.clientId === clientId);
    return role ? role.name : null;
  }, [rolesValues]);

  const findExpirationDate = useCallback(clientId => {
    const expirationDate = expirationDateValues.find(e => e.clientId === clientId);
    return expirationDate ? expirationDate.expirationDate : null;
  }, [expirationDateValues]);

  const onChangeExpirationDate = useCallback((currentClientId, value) => {
    const client = clients.find(({ clientId }) => clientId === currentClientId);
    changeExpirationDate(client, value);
  }, [clients, changeExpirationDate]);

  const { columnDefs } = useMemo(() => getColumnDefs({
    checkClient: onCheckClient,
    checkedClientsIds: value.map(({ clientId }) => clientId),
    editableClientId: Number(loggedInUser.lastClientId),
    clients,
    readOnly,
    rolesOptions,
    selectRole,
    changeExpirationDate: onChangeExpirationDate,
    disabled,
  }), [onCheckClient, value, loggedInUser, clients, readOnly, rolesOptions,
    selectRole, onChangeExpirationDate, disabled]);

  const rowData = useMemo(() => clients.map(option => ({
    clientId: option.clientId,
    name: option.name,
    role: findRole(option.clientId),
    expirationDate: findExpirationDate(option.clientId),
    expirationSsoName: option.expirationSsoName,
  })), [clients, findRole, findExpirationDate]);

  return (
    <div className="user-clients-grid">
      <AgGrid
        columnDefs={columnDefs}
        rowData={rowData}
        withSearchBar
        singleClickEdit
        headerHeight={60}
        onGridReady={onGridReady}
        isGridLoading={isLoading}
        {...(typeof document !== 'undefined' ? { popupParent: document.body } : {})}
      />
    </div>
  );
};

UserClientsGrid.propTypes = {
  clients: userClientListPropTypes.isRequired,
  readOnly: PropTypes.bool.isRequired,
  value: userClientListPropTypes,
  checkClient: PropTypes.func.isRequired,
  selectRole: PropTypes.func.isRequired,
  rolesOptions: rolesOptionsPropTypes,
  rolesValues: PropTypes.arrayOf(clientRolePropTypes),
  onGridReady: PropTypes.func.isRequired,
  loggedInUser: PropTypes.shape({
    lastClientId: PropTypes.string,
  }),
  expirationDateValues: PropTypes.arrayOf(clientExpirationDatePropTypes),
  changeExpirationDate: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool.isRequired,
};

export default UserClientsGrid;
